import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, Observable, Subscription, timer } from 'rxjs';
import { finalize, startWith, switchMap } from 'rxjs/operators';

import { ApiResponseModel } from '../../../models/core/api-response.model';
import { ListTransactionModel } from '../../../models/list-transaction.model';
import { TransactionsResource } from '../../../services/resources/transactions.resource';
import { UserSessionService } from '../../../services/user-session.service';

@Component({
    selector: 'sl-pending-transactions',
    templateUrl: './pending-transactions.component.html',
    styleUrls: ['./pending-transactions.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PendingTransactionsComponent implements OnInit, OnDestroy {
    pendingTransactions: ListTransactionModel[] = [];
    isApproving = false;
    hasUnapproved = false;
    canApprove = false;
    displayedColumns: string[] = [
        'valueDate',
        'transactionTypeName',
        'outgoingName',
        'registeredNumberOfShares',
        'arrow',
        'incomingName',
        'approvedByUserUsername'
    ];

    private pendingTransactionsSubscription: Subscription;

    private readonly router = inject(Router);
    private readonly transactionsResource = inject(TransactionsResource);
    private readonly userSesionService = inject(UserSessionService);

    ngOnInit(): void {
        this.canApprove = this.userSesionService.hasApprovalRight;
        this.transactionsResource.pending().subscribe(resp => this.updateList(resp.value));
        this.pendingTransactionsSubscription = timer(0, 1000)
            .pipe(
                startWith(0),
                switchMap(() => this.transactionsResource.pending())
            )
            .subscribe(resp => this.updateList(resp.value));

        if (this.canApprove) {
            this.displayedColumns.push('action');
        }
    }

    ngOnDestroy(): void {
        if (!!this.pendingTransactionsSubscription) {
            this.pendingTransactionsSubscription.unsubscribe();
        }
    }

    approve(event: any, id: string): void {
        if (!this.canApprove) {
            return;
        }

        event.stopPropagation();

        this.isApproving = true;
        const index = this.pendingTransactions.findIndex(t => t.id === id);
        this.transactionsResource.approve(id).subscribe(() => {
            this.pendingTransactions.splice(index, 1);
            this.isApproving = false;
        });
    }

    approveAll(): void {
        if (!this.canApprove) {
            return;
        }

        const ids = this.pendingTransactions.map(t => t.id);

        if (ids.length > 0) {
            this.isApproving = true;
            const requests: Observable<ApiResponseModel<boolean>>[] = [];

            ids.forEach(id => requests.push(this.transactionsResource.approve(id)));

            forkJoin(requests)
                .pipe(
                    finalize(() => {
                        // We only do this to allow for the subscription to reload the pending transactions.
                        setTimeout(() => {
                            this.isApproving = false;
                        }, 1200);
                    })
                )
                .subscribe();
        }
    }

    goToView(item: ListTransactionModel): void {
        this.router.navigate(['transactions', item.id]);
    }

    private updateList(value: ListTransactionModel[]): void {
        this.pendingTransactions = value;
        this.hasUnapproved = this.pendingTransactions.some(t => !t.approvedByUserUsername);
    }
}
